import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';
import { useIsoLayoutEffect } from '../../lib/useIsoLayoutEffect';
import { UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';

const settledAlready = () => Promise.resolve(true);

/**
 * The route half of useUnsavedChangesGuard: while `dirty`, a navigation to another PATHNAME is held and
 * the Unsaved changes dialog asks first. While `held` (a save a card runs by its own button has not
 * answered), the navigation is held too, first with no dialog: once `settleHeld` says the save worked
 * it goes, or asks first if there are unsaved changes; when the save failed it is dropped, so the
 * person stays where the card shows why. Rendered by that hook, and only under a data router
 * (useBlocker throws anywhere else).
 *
 * Only the pathname counts. Everything that changes the search alone passes untouched, because the app
 * does a lot of it without leaving the page: switching workspace (`?ws=`, and TenantContext's own
 * `replace` that keeps it in sync), the Content Composer and the Generate dialogs (`?compose=`,
 * `?generate=`), and the settings tabs (`?tab=`), whose switches ask on their own.
 */
export function RouteChangeGuard({
  dirty,
  save,
  discard,
  canSave = true,
  saving = false,
  message,
  held = false,
  settleHeld = settledAlready,
}: {
  dirty: boolean;
  save: () => Promise<boolean>;
  discard: () => void;
  canSave?: boolean;
  saving?: boolean;
  message?: ReactNode;
  held?: boolean;
  settleHeld?: () => Promise<boolean>;
}) {
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => (dirty || held) && currentLocation.pathname !== nextLocation.pathname,
    [dirty, held],
  );
  const blocker = useBlocker(shouldBlock);
  const [busy, setBusy] = useState(false);
  // The blocker and `dirty` as they are NOW, for the answer to a save that comes after the render that
  // waited on it.
  const latest = useRef({ blocker, dirty });
  useIsoLayoutEffect(() => {
    latest.current = { blocker, dirty };
  });

  // A save is still running (or the hold that blocked has just ended): no dialog yet; wait for its
  // answer. A failure stays; a save that worked goes on, or, with changes unsaved, lets the dialog ask.
  // Never while the dialog's own Save and continue runs (`busy`): a card can be clean before that save
  // answers (usePlatformSettingsSave refetches after it writes), and the dialog goes on by itself once
  // it does. Going on here as well would leave twice, and the second time throws.
  const waitingOnSave = blocker.state === 'blocked' && !busy && (held || !dirty);
  useEffect(() => {
    if (!waitingOnSave) return;
    let current = true;
    void settleHeld().then((ok) => {
      const { blocker: now, dirty: dirtyNow } = latest.current;
      if (!current || now.state !== 'blocked') return;
      if (!ok) now.reset();
      else if (!dirtyNow) now.proceed();
    });
    return () => {
      current = false;
    };
  }, [waitingOnSave, settleHeld]);

  // Where the focus was when the navigation was held (the link or menu item that asked to leave), so
  // Keep editing can put it back. Read while rendering: the dialog moves the focus into itself before
  // any effect of this component runs.
  const opener = useRef<HTMLElement | null>(null);
  if (blocker.state !== 'blocked' || waitingOnSave) {
    opener.current = null;
    return null;
  }
  if (opener.current === null && typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
    opener.current = document.activeElement;
  }

  return (
    <UnsavedChangesDialog
      open
      message={message}
      saving={busy || saving}
      canSave={canSave}
      returnFocusTo={opener.current}
      onKeepEditing={() => blocker.reset()}
      onDiscard={() => {
        discard();
        blocker.proceed();
      }}
      onSave={async () => {
        setBusy(true);
        let ok = false;
        try {
          ok = await save();
        } catch {
          ok = false;
        }
        setBusy(false);
        // A failed save stays on the page, where the card shows why.
        if (ok) blocker.proceed();
        else blocker.reset();
      }}
    />
  );
}
